// @ts-check

/**
 * @typedef {Window & { paypal?: { createInstance: Function, [key: string]: any } }} WindowWithPayPal
 */

/**
 * @typedef {Object} PayPalInitConfig
 * @property {string} clientToken - Client token from server
 * @property {string[]} [components] - SDK components to load (default: ['paypal-payments'])
 * @property {string} [locale] - Locale for the SDK (default: 'es-MX')
 * @property {string} [pageType] - Type of page (default: 'checkout')
 * @property {boolean} [sandbox] - Whether to use sandbox environment (default: true, deprecated - use sdkUrl instead)
 * @property {string} sdkUrl - PayPal SDK URL (required, should come from getPayPalSDKConfig())
 */

/**
 * @typedef {Object} PayPalEligibilityMethods
 * @property {function(string): boolean} isEligible - Check if payment method is eligible
 * @property {function(string): Object} getDetails - Get details for eligible payment method
 */

/**
 * @typedef {Object} PayPalSessionCallbacks
 * @property {function(Object): void} onApprove - Called when payment is approved
 * @property {function(Object): void} [onShippingAddressChange] - Called when shipping address changes
 * @property {function(Object): void} [onShippingOptionsChange] - Called when shipping options change
 * @property {function(Object): void} [onCancel] - Called when payment is canceled
 * @property {function(Error): void} [onError] - Called when error occurs
 * @property {string} [orderId] - Order ID for eager order creation
 * @property {boolean} [commit] - true = 'Pay Now', false = 'Continue'
 */

/**
 * @typedef {Object} PayPalPresentationOptions
 * @property {('auto'|'modal'|'popup'|'redirect')} presentationMode - Payment flow presentation mode
 * @property {Object} [autoRedirect] - Auto redirect configuration
 * @property {boolean} [autoRedirect.enabled] - Enable auto redirect
 * @property {Object} [fullPageOverlay] - Full page overlay configuration
 * @property {boolean} [fullPageOverlay.enabled] - Enable full page overlay
 * @property {Object} [loadingScreen] - Loading screen configuration
 * @property {string} [loadingScreen.label] - Loading screen label ('connecting')
 */

/**
 * PayPal v6 SDK Engine - Handles all SDK operations for PayPal payments
 * @function PayPalEngine
 */
const PayPalEngine = () => {
  let sdkInstance = null;
  let initializingPromise = null;

  /**
   * Loads the PayPal SDK script dynamically, avoiding duplicates
   * @param {string} sdkUrl - PayPal SDK URL to load
   * @returns {Promise<void>} Resolves when script is loaded
   * @throws {Error} If script fails to load
   */
  const loadSDKScript = async (sdkUrl) => {
    // Check if window.paypal already exists (script already loaded)
    const win = /** @type {WindowWithPayPal} */ (window);
    if (win.paypal && typeof win.paypal.createInstance === 'function') {
      return Promise.resolve();
    }

    // Check if the script already exists in the DOM
    const existingScript = /** @type {HTMLScriptElement | null} */ (
      document.querySelector(`script[src="${sdkUrl}"]`)
    );
    if (existingScript) {
      // If already complete, resolve immediately
      // @ts-ignore - complete y readyState existen en HTMLScriptElement en runtime
      if (existingScript.complete || existingScript.readyState === 'complete') {
        return Promise.resolve();
      }
      // If loading, wait for it to finish
      return new Promise((resolve, reject) => {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener(
          'error',
          () => {
            // Remove failed script from DOM so future initialize() calls can retry cleanly
            // Otherwise, subsequent calls will find this failed script and wait forever
            existingScript.remove();
            reject(new Error(`Failed to load PayPal SDK from ${sdkUrl}`));
          },
          { once: true },
        );
      });
    }

    const script = document.createElement('script');
    script.src = sdkUrl;
    document.head.appendChild(script);

    return new Promise((resolve, reject) => {
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener(
        'error',
        () => {
          // Remove failed script from DOM so future initialize() calls can retry cleanly
          // Otherwise, subsequent calls will find this failed script and wait forever
          script.remove();
          reject(
            new Error(
              `Failed to load PayPal SDK from ${sdkUrl}. Please check your network connection and try again.`,
            ),
          );
        },
        { once: true },
      );
    });
  };

  /**
   * Initializes the PayPal SDK by loading the script and creating an instance
   * This method is idempotent and handles race conditions by reusing the same promise
   * if multiple calls occur concurrently.
   * @param {PayPalInitConfig} config - Configuration object
   * @returns {Promise<void>} Resolves when SDK is initialized
   * @throws {Error} If initialization fails
   */
  const initialize = async (config) => {
    // If already initialized, return immediately
    if (sdkInstance) {
      return;
    }

    // If initialization is in progress, wait for it
    if (initializingPromise) {
      return initializingPromise;
    }

    // Start new initialization
    initializingPromise = (async () => {
      if (!config?.clientToken) {
        throw new Error('PayPal clientToken is required');
      }

      const { clientToken, sdkUrl, ...sdkConfig } = config;

      if (!sdkUrl) {
        throw new Error(
          'PayPal sdkUrl is required. Ensure getPayPalSDKConfig() is used to get configuration.',
        );
      }

      try {
        // Create SDK instance with default configuration
        const defaultConfig = {
          components: ['paypal-payments'],
          locale: 'es-MX',
          pageType: 'checkout',
        };

        const instanceConfig = {
          ...defaultConfig,
          ...sdkConfig,
          clientToken,
        };

        await loadSDKScript(sdkUrl);

        // Guard against SDK not loaded on window
        const win = /** @type {WindowWithPayPal} */ (window);
        if (!win.paypal || typeof win.paypal.createInstance !== 'function') {
          throw new Error('PayPal SDK not loaded on window.');
        }

        sdkInstance = await win.paypal.createInstance(instanceConfig);
        // Clear promise after successful initialization
        initializingPromise = null;
      } catch (error) {
        // Clear promise on error so retry is possible
        initializingPromise = null;
        throw new Error(`Failed to initialize PayPal SDK: ${error.message}`);
      }
    })();

    return initializingPromise;
  };

  /**
   * Finds eligible payment methods using the SDK instance
   * @param {string} [currency] - Currency code (e.g., 'MXN', 'USD', 'BRL')
   * @returns {Promise<PayPalEligibilityMethods>} Eligibility methods object
   * @throws {Error} If SDK instance is not initialized or eligibility check fails
   */
  const findEligibleMethods = async (currency) => {
    if (!sdkInstance) {
      throw new Error('PayPal SDK instance not initialized. Call initialize() first.');
    }

    try {
      const options = currency ? { currency } : {};
      return await sdkInstance.findEligibleMethods(options);
    } catch (error) {
      throw new Error(`Failed to check PayPal eligibility: ${error.message}`);
    }
  };

  /**
   * Creates a PayPal one-time payment session
   * @param {PayPalSessionCallbacks} callbacks - Callback functions for payment flow
   * @returns {Object} PayPal payment session object
   * @throws {Error} If SDK instance is not initialized or session creation fails
   */
  const createPayPalOneTimePaymentSession = (callbacks) => {
    if (!sdkInstance) {
      throw new Error('PayPal SDK instance not initialized. Call initialize() first.');
    }

    if (!callbacks?.onApprove) {
      throw new Error('onApprove callback is required for PayPal payment session');
    }

    try {
      return sdkInstance.createPayPalOneTimePaymentSession(callbacks);
    } catch (error) {
      throw new Error(`Failed to create PayPal payment session: ${error.message}`);
    }
  };

  /**
   * Creates a Pay Later one-time payment session
   * @param {PayPalSessionCallbacks} callbacks - Callback functions
   * @param {string} productCode - Pay Later product code from eligibility
   * @returns {Object} PayPal Pay Later payment session
   * @throws {Error} If SDK instance is not initialized or session creation fails
   */
  const createPayLaterOneTimePaymentSession = (callbacks, productCode) => {
    if (!sdkInstance) {
      throw new Error('PayPal SDK instance not initialized. Call initialize() first.');
    }

    if (!callbacks?.onApprove) {
      throw new Error('onApprove callback is required for PayPal payment session');
    }

    if (!productCode) {
      throw new Error('productCode is required for Pay Later payment session');
    }

    try {
      return sdkInstance.createPayLaterOneTimePaymentSession({
        productCode,
        ...callbacks,
      });
    } catch (error) {
      throw new Error(`Failed to create Pay Later payment session: ${error.message}`);
    }
  };

  /**
   * Creates a PayPal Credit one-time payment session
   * @param {PayPalSessionCallbacks} callbacks - Callback functions
   * @returns {Object} PayPal Credit payment session
   * @throws {Error} If SDK instance is not initialized or session creation fails
   */
  const createPayPalCreditOneTimePaymentSession = (callbacks) => {
    if (!sdkInstance) {
      throw new Error('PayPal SDK instance not initialized. Call initialize() first.');
    }

    if (!callbacks?.onApprove) {
      throw new Error('onApprove callback is required for PayPal payment session');
    }

    try {
      return sdkInstance.createPayPalCreditOneTimePaymentSession(callbacks);
    } catch (error) {
      throw new Error(`Failed to create PayPal Credit payment session: ${error.message}`);
    }
  };

  /**
   * Starts the PayPal payment flow with presentation options
   * @param {Object} paymentSession - PayPal payment session object
   * @param {PayPalPresentationOptions} options - Presentation options
   * @param {Promise<string>|string} orderIdPromise - Order ID or promise that resolves to order ID
   * @returns {Promise<void>} Resolves when payment flow starts successfully
   * @throws {Error} If payment flow fails to start
   */
  const startPayment = async (
    paymentSession,
    options = { presentationMode: 'auto' },
    orderIdPromise,
  ) => {
    if (!paymentSession?.start || typeof paymentSession.start !== 'function') {
      throw new Error('Invalid PayPal payment session provided');
    }

    try {
      await paymentSession.start(options, orderIdPromise);
    } catch (error) {
      // Try fallback presentation modes if auto or popup fails
      if (options.presentationMode === 'auto' || options.presentationMode === 'popup') {
        try {
          console.warn(
            `PayPal initial attempt with presentationMode '${options.presentationMode}' failed, trying modal fallback:`,
            error.message,
          );
          await paymentSession.start({ presentationMode: 'modal' }, orderIdPromise);
        } catch (popupError) {
          console.warn(
            'PayPal modal fallback failed, trying redirect fallback:',
            popupError.message,
          );
          try {
            await paymentSession.start({ presentationMode: 'redirect' }, orderIdPromise);
          } catch (redirectError) {
            throw new Error(
              `Failed to start PayPal payment flow after fallbacks: ${redirectError.message}`,
            );
          }
        }
        return;
      }
      throw new Error(`Failed to start PayPal payment flow: ${error.message}`);
    }
  };

  /**
   * Checks if the SDK instance is initialized
   * @returns {boolean} True if SDK instance is initialized, false otherwise
   */
  const isInitialized = () => Boolean(sdkInstance);

  /**
   * Gets the current SDK instance
   * @returns {Object} Current SDK instance
   * @throws {Error} If SDK instance is not initialized
   */
  const getInstance = () => {
    if (!sdkInstance) {
      throw new Error('PayPal SDK instance not initialized. Call initialize() first.');
    }
    return sdkInstance;
  };

  return {
    initialize,
    findEligibleMethods,
    createPayPalOneTimePaymentSession,
    createPayLaterOneTimePaymentSession,
    createPayPalCreditOneTimePaymentSession,
    startPayment,
    isInitialized,
    getInstance,
  };
};

// Create singleton instance
const paypalEngine = PayPalEngine();

export default paypalEngine;
